import React, { useEffect, useMemo, useState } from "react";
import { getCalendar, listMood, listCycleLogs } from "../lib/api";
import { motion } from "framer-motion";

/* Horizontal cinematic hormone timeline:
   - Phase-colored strip for each day (last N days)
   - Mood dot below
   - Period marker
   - Smooth horizontal scroll with snapping
   This is the "story of your body" view \u2014 differentiator vs Flo/Clue. */

const PHASE_COLORS = {
  period:    "linear-gradient(180deg, hsl(6 58% 64%) 0%, hsl(6 58% 50%) 100%)",
  fertile:   "linear-gradient(180deg, hsl(142 32% 60%) 0%, hsl(142 32% 46%) 100%)",
  ovulation: "linear-gradient(180deg, hsl(332 36% 64%) 0%, hsl(332 36% 50%) 100%)",
  default:   "linear-gradient(180deg, hsl(36 22% 86%) 0%, hsl(36 22% 78%) 100%)",
};

const MOOD_COLORS = {
  great: "hsl(142 32% 50%)",
  good:  "hsl(44 70% 56%)",
  okay:  "hsl(36 30% 60%)",
  low:   "hsl(268 30% 58%)",
  awful: "hsl(6 58% 56%)",
};

function fmtMonthDay(d) {
  return d.toLocaleString("default", { month: "short", day: "numeric" });
}
function fmtWeekday(d) {
  return d.toLocaleString("default", { weekday: "short" });
}

export default function HormoneTimeline({ days = 30 }) {
  const [calendarDays, setCalendarDays] = useState([]);
  const [moods, setMoods] = useState({});
  const [symptomDays, setSymptomDays] = useState({}); // { isoDate: [symptom, ...] }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const today = new Date();
        const cur = { y: today.getFullYear(), m: today.getMonth() + 1 };
        const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const prv = { y: prev.getFullYear(), m: prev.getMonth() + 1 };
        const [calA, calB, moodList, cycleLogs] = await Promise.all([
          getCalendar(prv.y, prv.m),
          getCalendar(cur.y, cur.m),
          listMood(),
          listCycleLogs(),
        ]);
        const allDays = [...(calA?.days || []), ...(calB?.days || [])];
        allDays.sort((a, b) => (a.date < b.date ? -1 : 1));
        setCalendarDays(allDays);

        const moodMap = {};
        (moodList || []).forEach((m) => {
          if (m?.date && m?.mood) moodMap[m.date] = m.mood;
        });
        setMoods(moodMap);

        const sympMap = {};
        (cycleLogs || []).forEach((c) => {
          if (c?.start_date && Array.isArray(c.symptoms) && c.symptoms.length > 0) {
            sympMap[c.start_date] = c.symptoms;
          }
        });
        setSymptomDays(sympMap);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const timelineDays = useMemo(() => {
    if (calendarDays.length === 0) return [];
    // Build day window: last `days` days up to today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const window = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      const found = calendarDays.find((x) => x.date === iso);
      window.push({
        date: iso,
        day: d.getDate(),
        weekday: fmtWeekday(d),
        label: fmtMonthDay(d),
        tag: found?.tag || null,
        is_today: found?.is_today || iso === today.toISOString().slice(0, 10),
        mood: moods[iso] || null,
        symptoms: symptomDays[iso] || null,
      });
    }
    return window;
  }, [calendarDays, moods, symptomDays, days]);

  return (
    <div className="surface-cream plush-shadow-sm rounded-[28px] p-5 overflow-hidden relative" data-testid="hormone-timeline">
      <div className="flex items-end justify-between mb-1">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] font-semibold opacity-60">Hormone story</p>
          <p className="font-editorial text-[22px] leading-tight mt-1">The last {days} days</p>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-[hsl(var(--muted-foreground))] font-semibold">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "hsl(6 58% 56%)" }} /> Period</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "hsl(142 32% 50%)" }} /> Fertile</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "hsl(332 36% 56%)" }} /> Ovulation</span>
        </div>
      </div>

      <div className="mt-3 -mx-5 px-5 overflow-x-auto scrollbar-thin" style={{ scrollbarWidth: "thin" }}>
        <div className="flex items-end gap-1.5 pb-2 pt-1" style={{ minWidth: `${timelineDays.length * 18}px` }}>
          {loading && (
            <div className="flex gap-1.5">
              {Array.from({ length: days }).map((_, i) => (
                <div key={i} className="shimmer w-3 h-24 rounded-full" />
              ))}
            </div>
          )}
          {!loading && timelineDays.map((d, idx) => {
            const gradient = PHASE_COLORS[d.tag] || PHASE_COLORS.default;
            const hasSymptoms = d.symptoms && d.symptoms.length > 0;
            return (
              <motion.div
                key={d.date}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.012 }}
                className="flex flex-col items-center gap-1.5"
                data-testid={`timeline-day-${d.day}`}
                title={hasSymptoms ? `${d.label}: ${d.symptoms.join(", ")}` : d.label}
              >
                {/* Symptom notch above the bar */}
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    background: hasSymptoms ? "hsl(var(--gold))" : "transparent",
                    boxShadow: hasSymptoms ? "0 0 0 2px hsl(var(--background))" : "none",
                  }}
                />
                {/* Phase strip */}
                <div
                  className="w-3 rounded-full relative"
                  style={{
                    height: 96,
                    background: gradient,
                    boxShadow: d.is_today ? "0 0 0 2px hsl(var(--plum)), 0 0 0 4px hsl(var(--background))" : "inset 0 -4px 6px rgba(0,0,0,0.08)",
                  }}
                />
                {/* Mood dot */}
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{
                    background: d.mood ? MOOD_COLORS[d.mood] || MOOD_COLORS.okay : "transparent",
                    border: d.mood ? "none" : "1px dashed hsl(30 22% 78%)",
                  }}
                  title={d.mood ? `Mood: ${d.mood}` : "No mood logged"}
                />
                {/* Day number (only every 5th to reduce density) */}
                <p className={`text-[9px] font-semibold tracking-wide ${d.is_today ? "text-[hsl(var(--plum))]" : "text-[hsl(var(--muted-foreground))]"}`}>
                  {idx % 5 === 0 || d.is_today ? d.day : ""}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>

      <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2 leading-snug font-body">
        Each bar reflects one day. Color shows your phase, the dot beneath captures the mood you logged, and the gold notch above marks days with symptoms.
      </p>

      {/* Symptom highlights summary */}
      {(() => {
        const symptomList = timelineDays.filter((d) => d.symptoms && d.symptoms.length > 0);
        if (symptomList.length === 0) return null;
        // Tally symptom frequency
        const tally = {};
        symptomList.forEach((d) => d.symptoms.forEach((s) => { tally[s] = (tally[s] || 0) + 1; }));
        const topSymptoms = Object.entries(tally).sort((a, b) => b[1] - a[1]).slice(0, 6);
        return (
          <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]" data-testid="timeline-symptom-summary">
            <p className="text-[10px] uppercase tracking-[0.24em] font-semibold opacity-60 mb-2">Symptoms this window</p>
            <div className="flex flex-wrap gap-1.5">
              {topSymptoms.map(([s, n]) => (
                <span key={s} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[hsl(var(--blush))] text-[hsl(338_38%_24%)] text-[11px] font-semibold">
                  {s} <span className="opacity-60">×{n}</span>
                </span>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
